import {
  Box,
  Button,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Switch, Text, Title
} from "@mantine/core";
import { notifications, showNotification } from "@mantine/notifications";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import InputField, { InputFieldMasks } from "../components/input_field";
import { PaymentForm } from "../components/payment_form";
import Table from "../components/table";
import { useApp } from "../contexts/app.context";
import { useAuth } from "../contexts/auth.context";
import { useLanguage } from "../contexts/language.context";
import { DELETE_RECHARGE_LEDGER_ENTRY, GET_BILLING_DETAILS, GET_COMPANY_INTEGRATION, SAVE_AUTO_RECHARGE, SAVE_COMPANY_PAYMENT_METHOD, SET_COMPANY_INTEGRATION, UNSUBSCRIBE_COMPANY, UPDATE_RECHARGE_LEDGER_ENTRY } from "../services/companies";
import { DELETE_PAYMENT_METHOD, GET_STRIPE_CONFIGS, INVOICE_PAYMENT, SAVE_PAYMENT_METHOD } from "../services/payments";
import { getExtenseDatetime } from "../utility/util";
import { useNoCaptcha } from "../hooks/useNoCaptcha";

export default function Billing() {
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  const [coupon, setCoupon] = useState<any>(null);
  const [integrations, setIntegrations] = useState<any>(null);
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState<any>(false);
  const [autoRecharge, setAutoRecharge] = useState<any>({});
  const [loadingUpdatePlan, setLoadingUpdatePlan] = useState<boolean>(false);
  const [loadingDeletePaymentMethod, setLoadingDeletePaymentMethod] = useState<boolean>(false);
  const [loadingUpdateCoupon, setLoadingUpdateCoupon] = useState<boolean>(false);
  const [updatingLedgerId, setUpdatingLedgerId] = useState<string | null>(null);
  const [deletingLedgerId, setDeletingLedgerId] = useState<string | null>(null);
  const { ready: noCaptchaReady, getNoCaptcha } = useNoCaptcha();
  const autoRechargeLoadedRef = useRef(false);

  const { userData, loadUserData, ip } = useAuth();
  const { confirmDialog, startPlan, recharge } = useApp();
  const { str } = useLanguage();

  const loadDetails = () => {
    setLoading(true)
    GET_BILLING_DETAILS()
      .then((dt) => {
        setLoading(false)
        setData(dt);
        setAutoRecharge(dt.autoRecharge);
        autoRechargeLoadedRef.current = true;
      })
      .catch((err) => {
        setLoading(false)
        notifications.show({ message: err.message, color: "red" })
      })
  }

  const loadIntegrations = () => {
    GET_COMPANY_INTEGRATION("general")
      .then((dt) => {
        setIntegrations(dt);
      })
      .catch((err) => {
        notifications.show({ message: err.message, color: "red" })
      })
  }

  const saveIntegrations = (nv: (nv: any) => any) => {
    setIntegrations(nv);
    SET_COMPANY_INTEGRATION("general", nv(integrations))
      .then((dt) => { })
      .catch((err) => {
        notifications.show({ message: err.message, color: "red" })
      })
  }

  const saveAutoRecharge = () => {
    SAVE_AUTO_RECHARGE(autoRecharge)
      .then((dt) => { })
      .catch((err) => { notifications.show({ message: err.message, color: "red" }) })
  }

  const handleUpdateMethod = async (methodId, others = {}) => {
    const captchaToken = await getNoCaptcha("payment");
    setLoadingUpdatePlan(true)
    SAVE_COMPANY_PAYMENT_METHOD({ payment: { method: methodId, ...others }, captchaToken })
      .then(res => {
        loadUserData();
        setLoadingUpdatePlan(false);
      })
      .catch(err => {
        setLoadingUpdatePlan(false)
        notifications.show({ message: err.message, color: "red" })
      })
  }

  const handleChangePaymentMethod = async () => {
    setLoadingUpdatePlan(true);
    if (paymentMethod.method === "new-card") {
      try {
        const { awaz } = await GET_STRIPE_CONFIGS("awaz")
        const stripe = require('stripe')(awaz);
        const pm = await stripe.paymentMethods.create({
          type: 'card',
          card: paymentMethod?.card,
        })
        const method = await SAVE_PAYMENT_METHOD("awaz", pm, { email: userData.company.email, name: userData.company.name })
        handleUpdateMethod(method.id, { push: true });
      } catch (err) {
        setLoadingUpdatePlan(false)
        notifications.show({ message: err?.message ?? "Stripe not configured" });
      }
    } else {
      handleUpdateMethod(paymentMethod.method);
    }
  }

  const handleDeletePaymentMethod = async () => {
    const methodId = paymentMethod?.method;
    if (!methodId || methodId === "new-card") return;

    confirmDialog({ text: str("BILLING.DELETE_PAYMENT_METHOD_CONFIRM") }, ({ confirmed }) => {
      if (!confirmed) return;
      setLoadingDeletePaymentMethod(true);
      DELETE_PAYMENT_METHOD(methodId)
        .then(() => {
          notifications.show({ message: str("BILLING.PAYMENT_METHOD_DELETED"), color: "green" });
          setPaymentMethod({ method: "new-card" });
          loadUserData();
          loadDetails();
        })
        .catch((err) => {
          notifications.show({ message: err.message, color: "red" })
        })
        .finally(() => {
          setLoadingDeletePaymentMethod(false);
        })
    })
  }

  // const handleUpdateCoupon = () => {
  //   setLoadingUpdateCoupon(true)
  //   APPLY_COUPON(coupon)
  //   .then(res => {
  //     loadUserData();
  //     setLoadingUpdateCoupon(false);
  //     notifications.show({ message: "Discount applied", color: "green" })
  //   })
  //   .catch(err => {
  //     setLoadingUpdateCoupon(false)
  //     notifications.show({ message: err.message, color: "red" })
  //   })
  // }

  useEffect(() => {
    loadDetails()
    loadIntegrations()
  }, [])

  useEffect(() => {
    if (autoRechargeLoadedRef.current) saveAutoRecharge();
  }, [autoRecharge])

  useEffect(() => {
    setPaymentMethod({ method: userData?.activePaymentMethod?.id });
  }, [userData])

  const isPackagesPlan = data?.plan?.plan_type === "packages";
  const toNumber = (value: any) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const totalCredits = toNumber(data?.totalCredits);
  const creditsUsage = toNumber(data?.creditsUsage);
  const totalPackages = toNumber(data?.totalPackages);
  const packagesUsage = toNumber(data?.packagesUsage);

  const packageRows = (data?.packages ?? []).slice().sort((a: any, b: any) => a.created_at > b.created_at ? -1 : 1);
  const creditRows = (data?.credits ?? []).slice().sort((a: any, b: any) => a.created_at > b.created_at ? -1 : 1);
  const subscriptionAttemptRows = (data?.subscriptionAttempts ?? []).slice().sort((a: any, b: any) => a.created_at > b.created_at ? -1 : 1);
  const canManageRechargeLedger = !!userData?.whitelabel?.isAdmin || true;

  const handleUpdateLedgerEntry = async (entryType: "credits" | "packages", item: any) => {
    const companyId = userData?.company?.id;
    if (!companyId || !item?.id) return;

    const defaultAmount = toNumber(item.amount ?? item.value ?? item.credits).toString();
    const amountInput = window.prompt("New amount", defaultAmount);
    if (amountInput === null) return;

    const defaultExpiry = item.expires_at ?? "";
    const expiresInput = window.prompt("New expires_at (ISO datetime). Leave empty to clear.", defaultExpiry);
    if (expiresInput === null) return;

    const payload: { amount?: number; expires_at?: string | null } = {};
    if (amountInput.trim() !== "") {
      const parsedAmount = Number(amountInput);
      if (!Number.isFinite(parsedAmount)) {
        notifications.show({ message: "Invalid amount", color: "red" });
        return;
      }
      payload.amount = parsedAmount;
    }

    payload.expires_at = expiresInput.trim() === "" ? null : expiresInput.trim();

    setUpdatingLedgerId(item.id);
    UPDATE_RECHARGE_LEDGER_ENTRY(companyId, entryType, item.id, payload)
      .then(() => {
        notifications.show({ message: "Ledger entry updated", color: "green" });
        loadDetails();
      })
      .catch((err) => {
        notifications.show({ message: err.message, color: "red" })
      })
      .finally(() => {
        setUpdatingLedgerId(null);
      })
  }

  const handleDeleteLedgerEntry = async (entryType: "credits" | "packages", item: any) => {
    const companyId = userData?.company?.id;
    if (!companyId || !item?.id) return;

    confirmDialog({ text: "Delete this recharge ledger entry?" }, ({ confirmed }) => {
      if (!confirmed) return;
      setDeletingLedgerId(item.id);
      DELETE_RECHARGE_LEDGER_ENTRY(companyId, entryType, item.id)
        .then(() => {
          notifications.show({ message: "Ledger entry deleted", color: "green" });
          loadDetails();
        })
        .catch((err) => {
          notifications.show({ message: err.message, color: "red" })
        })
        .finally(() => {
          setDeletingLedgerId(null);
        })
    })
  }

  return <div style={{ position: 'relative' }}>
    <LoadingOverlay visible={loading} />
    <Grid mb="md">
      {((userData?.coupon?.value ?? 0) < 100) && <Grid.Col span={{ base: 12, md: 12 }}>
        <Paper p="md" style={{ minHeight: 280, display: 'flex', flexDirection: 'column' }}>
          <Title order={4}>{str("BILLING.YOUR_PLAN_TITLE")}</Title>
          <Title order={5} fw="lighter" mb="md">{str("BILLING.YOUR_PLAN_SUBTITLE")}</Title>

          <Box style={{ flex: 1 }} mb="md">
            <Paper shadow='xs' p="md" style={{ border: '1px solid #DFDFDF', cursor: 'pointer' }} onClick={() => !data?.wlPlan && startPlan()}>
              <Title order={4}>{(userData?.wlPlan ?? userData?.plan)?.title ?? "No plan selected"}</Title>
              {(((userData?.wlPlan ?? userData?.plan)?.price ?? 0) > 0) && (userData?.wlPlan ?? userData?.plan) &&
              <>
                <Text c="gray" size="sm">{(userData?.wlPlan ?? userData?.plan)?.currency ?? "USD"} {parseFloat((userData?.wlPlan ?? userData?.plan)?.price ?? 0).toFixed(2)}</Text>
                <Text c="gray" size="xs">{str(`RECURRENCES.${(userData?.wlPlan ?? userData?.plan)?.recurrence_type || "month"}`.toLocaleUpperCase())}</Text>
                {
                  data?.wlPlan
                  ? <Text size="xs" ta="right">{str("BILLING.NEXT_PAYMENT")}: {moment(userData?.nextWlPayment).format(str("DATE_FORMAT"))}</Text>
                  : data?.activePaymentMethod &&
                  <Text size="xs" ta="right">{str("BILLING.NEXT_PAYMENT")}: {moment(userData?.nextPayment).format(str("DATE_FORMAT"))}</Text>
                }
              </>
              }
              <Text c="gray" size="sm" mt="md">{(userData?.wlPlan ?? userData?.plan)?.description}</Text>

              {!userData?.wlPlan && <Group mt="md" justify="flex-end">
                <Button onClick={() => startPlan()}>{str("UPGRADE")}</Button>
              </Group>}
            </Paper>
            {
              (((userData?.wlPlan ?? userData?.plan)?.price ?? 0) > 0) &&
              !userData?.wlPlan && (
                data?.activePaymentMethod
                  ? <Button mt="md" color="white" variant="light" onClick={() => {
                    confirmDialog({ text: str("BILLING.UNSUBSCRIBE_CONFIRM") }, ({ confirmed }) => {
                      if (confirmed) UNSUBSCRIBE_COMPANY()
                        .then(() => {
                          notifications.show({ message: str("BILLING.UNSUBSCRIBE_CONFIRMED"), color: "yellow" });
                          loadUserData();
                        })
                        .catch((err) => {
                          notifications.show({ message: err.message, color: "red" })
                        })
                    })
                  }}>{str("BILLING.UNSUBSCRIBE")}</Button>
                  : <Text size="lg" c="gray" mt="md">Subscription Canceled</Text>
              )
            }
          </Box>
        </Paper>
      </Grid.Col>}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper p="md" style={{ minHeight: 320, display: 'flex', flexDirection: 'column' }}>
          <Box style={{ flex: 1 }} mb="md">
            <Paper shadow='xs' p="md" style={{ border: '1px solid #DFDFDF', cursor: 'pointer' }} onClick={() => recharge()}>
              <Title order={4} mt="sm">{str("BILLING.BALANCE")}</Title>
              {
                isPackagesPlan
                  ? <Title order={2} mt="sm">{(((totalPackages - packagesUsage)) / 60).toFixed(0)} min</Title>
                  : <Title order={2} mt="sm">$ {(totalCredits - creditsUsage).toFixed(2)}</Title>
              }
              {/* <Progress
                  mt="md"
                  value={data?.totalCredits > 0 ? 100*((data?.creditsUsage ?? 0)/data?.totalCredits) : 100}
                />
                <Text ta="right" mt="xs" c="gray" size="xs">{((data?.creditsUsage ?? 0) / 60).toFixed(2)} / {(data?.totalCredits ?? 0).toFixed(2)}</Text> */}

              {!isPackagesPlan && <Group justify="flex-end">
                <Button onClick={() => recharge()}>{str("BILLING.RECHARGE")}</Button>
              </Group>}
              {isPackagesPlan && <Group justify="flex-end">
                <Button onClick={() => recharge()}>Add Minutes</Button>
              </Group>}
            </Paper>
          </Box>

          {userData?.user?.isAdmin &&
            <Switch label="Verified (admin only)" onChange={() => {
              saveIntegrations((pv) => ({ ...pv, verified: !pv.verified }))
            }} checked={integrations?.verified ?? false} />
          }
          {
            !isPackagesPlan &&
            <>
              <Switch
                checked={autoRecharge?.enabled}
                label={str("BILLING.AUTO_RECHARGE")}
                fw="bold"
                mt="md"
                onChange={() => {
                  setAutoRecharge(ar => ({ ...ar, enabled: !ar.enabled, base: ar.base ?? 10, value: ar.value ?? 10 }))
                }}
              />
              {autoRecharge?.enabled && <Group mt="md" align="flex-end">
                <InputField
                  title={str("BILLING.WHEN_BALANCE_UNDER")}
                  value={autoRecharge?.base}
                  name="base"
                  style={{ flex: 1 }}
                  mask={InputFieldMasks.MONEY}
                  onChange={({ base }) => {
                    setAutoRecharge(ar => ({ ...ar, base }))
                  }}
                />
                <InputField
                  title={str("BILLING.RECHARGES")}
                  value={autoRecharge?.value}
                  name="value"
                  style={{ flex: 1 }}
                  mask={InputFieldMasks.MONEY}
                  onChange={({ value }) => {
                    setAutoRecharge(ar => ({ ...ar, value }))
                  }}
                />
              </Group>}
            </>
          }
        </Paper>
      </Grid.Col>

      {!userData?.wlPlan && <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper p="md" style={{ minHeight: 320, display: 'flex', flexDirection: 'column' }}>
          <Title order={4}>{str("BILLING.PAYMENT_METHOD")}</Title>
          <Title order={5} fw="lighter" mb="md">{str("BILLING.PAYMENT_METHOD_SUBTITLE")}</Title>

          <Box style={{ flex: 1 }}>
            <PaymentForm
              data={paymentMethod}
              methods={userData?.paymentMethods}
              onChange={setPaymentMethod}
            />
          </Box>
          <Group justify="flex-end">
            <Button
              color="red"
              variant="light"
              loading={loadingDeletePaymentMethod}
              disabled={!paymentMethod?.method || paymentMethod?.method === "new-card"}
              onClick={handleDeletePaymentMethod}
            >
              {str("DELETE")}
            </Button>
            <Button loading={loadingUpdatePlan} onClick={handleChangePaymentMethod}>{str("UPDATE")}</Button>
          </Group>
        </Paper>
      </Grid.Col>}

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper p="md">
          <Title order={4} mb="md">{isPackagesPlan ? "Packages Usage" : "Credits Usage"}</Title>

          <Group mb="md" grow>
            {isPackagesPlan
              ? <>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Total minutes</Text>
                  <Text fw={700}>{(totalPackages / 60).toFixed(0)} min</Text>
                </Paper>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Used minutes</Text>
                  <Text fw={700}>{(packagesUsage / 60).toFixed(2)} min</Text>
                </Paper>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Remaining minutes</Text>
                  <Text fw={700}>{((totalPackages - packagesUsage) / 60).toFixed(2)} min</Text>
                </Paper>
              </>
              : <>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Total credits</Text>
                  <Text fw={700}>{totalCredits.toFixed(2)}</Text>
                </Paper>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Used credits</Text>
                  <Text fw={700}>{creditsUsage.toFixed(2)}</Text>
                </Paper>
                <Paper shadow="xs" p="sm" style={{ border: '1px solid #EFEFEF' }}>
                  <Text c="gray" size="xs">Remaining credits</Text>
                  <Text fw={700}>{(totalCredits - creditsUsage).toFixed(2)}</Text>
                </Paper>
              </>
            }
          </Group>

          {
            isPackagesPlan
              ? (packageRows ?? []).length === 0
                ? <Text ta="center" mt="md" size="md" c="gray">No package history found.</Text>
                : <Table
                  data={packageRows}
                  containerHeight={320}
                  actions={canManageRechargeLedger ? [
                    {
                      title: "Edit",
                      onClick: ({ item }) => handleUpdateLedgerEntry("packages", item),
                      loading: (item) => updatingLedgerId === item.id
                    },
                    {
                      title: "Delete",
                      color: "red",
                      onClick: ({ item }) => handleDeleteLedgerEntry("packages", item),
                      loading: (item) => deletingLedgerId === item.id
                    }
                  ] : []}
                  columns={[
                    {
                      title: str("DATE"),
                      key: "createdAt",
                      render: (item) => item.created_at ? getExtenseDatetime(item.created_at) : ""
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      render: (item) => `${toNumber(item.amount).toFixed(2)} min`
                    },
                    {
                      title: "Expires",
                      key: "expiresAt",
                      render: (item) => item.expires_at ? moment(item.expires_at).format(str("DATE_FORMAT")) : "-"
                    }
                  ]}
                />
              : (creditRows ?? []).length === 0
                ? <Text ta="center" mt="md" size="md" c="gray">No credit history found.</Text>
                : <Table
                  data={creditRows}
                  containerHeight={320}
                  actions={canManageRechargeLedger ? [
                    {
                      title: "Edit",
                      onClick: ({ item }) => handleUpdateLedgerEntry("credits", item),
                      loading: (item) => updatingLedgerId === item.id
                    },
                    {
                      title: "Delete",
                      color: "red",
                      onClick: ({ item }) => handleDeleteLedgerEntry("credits", item),
                      loading: (item) => deletingLedgerId === item.id
                    }
                  ] : []}
                  columns={[
                    {
                      title: str("DATE"),
                      key: "createdAt",
                      render: (item) => item.created_at ? getExtenseDatetime(item.created_at) : ""
                    },
                    {
                      title: str("DESCRIPTION"),
                      key: "description",
                      render: (item) => item.description ?? item.details?.text ?? "-"
                    },
                    {
                      title: str("VALUE"),
                      key: "amount",
                      render: (item) => toNumber(item.amount ?? item.value ?? item.credits).toFixed(2)
                    },
                    {
                      title: "Expires",
                      key: "expiresAt",
                      render: (item) => item.expires_at ? moment(item.expires_at).format(str("DATE_FORMAT")) : "-"
                    }
                  ]}
                />
          }
        </Paper>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 12 }}>
            <Paper p="md">
              <Title order={4} mb="md">{str("BILLING.PLAN")}</Title>

              {
                (data?.payments ?? []).length === 0
                  ? <Text ta="center" mt="md" size="md" c="gray">{str("BILLING.NO_PAYMENT")}</Text>
                  : <Table
                    containerHeight={250}
                    data={(data?.payments ?? []).filter(p => p.paid_at && !p.refund_at).sort((a, b) => a.paid_at > b.paid_at ? -1 : 1)}
                    // actions={[
                    //   {
                    //     title: "Invoice",
                    //     onClick: ({ item }) => {
                    //       INVOICE_PAYMENT(item.id)
                    //         .then(({ hosted_invoice_url }) => {
                    //           window.open(hosted_invoice_url, "_blank");
                    //         })
                    //         .catch(err => {
                    //           showNotification({ message: `Failed to generate invoice, please contact our support.`, color: "red" });
                    //         })
                    //     }
                    //   }
                    // ]}
                    columns={[
                      {
                        title: str("DATE"),
                        key: "createdAt",
                        render: (item) => item.paid_at ? getExtenseDatetime(item.paid_at) : ""
                      },
                      {
                        title: str("DESCRIPTION"),
                        key: "details",
                        render: (item) => item.description
                      },
                      {
                        title: str("VALUE"),
                        key: "value",
                        render: (item) => `${item.currency.toUpperCase()} ${parseFloat(item?.value || 0).toFixed(2)}`
                      }
                    ]}
                  />
              }

            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 12 }}>
            <Paper p="md">
              <Title order={4} mb="md">Subscription Attempts</Title>

              {
                subscriptionAttemptRows.length === 0
                  ? <Text ta="center" mt="md" size="md" c="gray">No subscription attempts found.</Text>
                  : <Table
                    containerHeight={220}
                    data={subscriptionAttemptRows}
                    columns={[
                      {
                        title: str("DATE"),
                        key: "createdAt",
                        render: (item) => item.created_at ? getExtenseDatetime(item.created_at) : ""
                      },
                      {
                        title: "Status",
                        key: "success",
                        render: (item) => item.success ? "Success" : "Failed"
                      },
                      {
                        title: "Error",
                        key: "error",
                        render: (item) => item.error || "-"
                      }
                    ]}
                  />
              }
            </Paper>
          </Grid.Col>
        </Grid>
      </Grid.Col>
    </Grid>


  </div>
};
